import { ProductVersionStatus } from '../enums/product-version-status.enum';

/** Classification of a program on the Fagor 8050 M (Motionmaster 5-axis router). */
export enum MotionmasterProgramKind {
  Utility = 'utility', // bit change, park, warm-up, centering jig
  Sub = 'sub', // SUB routines called by part programs (XY HOLE, ...)
  Template = 'template', // PRESET PROGRAM
  Product = 'product', // cuts a part
  Service = 'service', // axis following / drill bushing checks
  Plc = 'plc', // PLC_ERR / PLC_MSG / PLC_PRG
  Doc = 'doc', // M-code list, reference
  Test = 'test', // test cuts and P777xxx trials
  Job = 'job', // a table job: Prism-generated main program that places sub programs on grid cells
  Obsolete = 'obsolete', // superseded by another program number
  Unknown = 'unknown',
}

/** Where a version's text came from. */
export enum MotionmasterProgramVersionSource {
  Import = 'import', // one-time seed from the 2026-10-15 control backup
  ControlPull = 'control-pull', // read from the control by the bridge (catches pendant edits)
  Prism = 'prism', // written in Prism and pushed to the control
}

/** Stable identity: one per program number on the control. The editable definition lives on a version. */
export interface MotionmasterProgram {
  id: string;
  programNumber: number;
  kind: MotionmasterProgramKind;
  /** true once the program has been deleted from the control */
  archived: boolean;
  createdById: string;
  createdOn: Date;
  /**
   * The table cell this program's coordinates were written for (0-based column along +machine X,
   * 1-based row along +machine Y). Null until the fixture's cell has been confirmed at the machine;
   * only programs with a home cell can be placed on a job.
   */
  homeColumn?: number | null;
  homeRow?: number | null;
  /** Cells the fixture covers from its home cell toward +X and +Y; null when unknown. */
  footprintColumns?: number | null;
  footprintRows?: number | null;
}

/**
 * The one machine table: how many cells, how far apart, and where cell A1 sits in machine
 * coordinates once that has been measured. Until then placements are relative to each sub's home cell.
 */
export interface MotionmasterTable {
  id: string;
  columns: number;
  rows: number;
  /** Inches between neighbouring cells. */
  pitch: number;
  originX: number | null;
  originY: number | null;
  /** The zero offset a job sets for each placement, 54-59. */
  offsetCode: number;
  measuredOn: Date | null;
  /**
   * Machine Z of the table top as the tool tip sees it (the toolpath viewer's frame), or null until touched off.
   * 2026-10-28: with the head horizontal (V-90) at Z-25.27 the cutter axis was 13.125" above the table, so -38.4.
   */
  surfaceZ?: number | null;
  surfaceMeasuredOn?: Date | null;
  updatedById: string;
  updatedOn: Date;
}

/**
 * Status meaning for programs: Released = the text currently on the control,
 * Obsolete = was on the control before, Draft = edited in Prism and not pushed yet.
 */
export interface MotionmasterProgramVersion {
  id: string;
  programId: string;
  versionNumber: number;
  status: ProductVersionStatus;
  /** the control's comment field (max 20 characters on the 8050) */
  name: string;
  description: string;
  /** full program text, LF line endings, `%name,MX,` header on the first line */
  text: string;
  /** size as the control's directory reports it (or the text length when unknown) */
  sizeBytes: number;
  /** sha256 hex of `text` */
  digest: string;
  /** raw `dd/mm/yy hh:mm:ss` from the control's directory; its clock is wrong so this is not a date */
  controlStamp: string;
  source: MotionmasterProgramVersionSource;
  sourceVersionId: string;
  createdById: string;
  createdOn: Date;
  updatedById: string;
  updatedOn: Date;
  releasedById: string;
  releasedOn: Date | null;
  releaseComment: string;
  /** Jobs only: the placements this text was generated from, as JSON (see MotionmasterJobLayout). */
  layout?: string;
}
